// MACHINERY V2 (Stroje) — V4 shop-floor wall: the status-specific tile body
// (M2.14). The text atoms it composes live in wallTileAtoms.tsx (split at 330
// lines, over the 300-line cap — see that file's header).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.14 + desktop/README.md §V4.
// Reference impl: desktop/machinery-v4-wall.jsx (the five branches of V4Tile).
// Rendered truth: desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png (both viewed before this file was written).
//
// ============================================================
//  >>> DATA HONESTY IS THE WHOLE POINT OF THIS FILE. <<<
// ============================================================
// The manager's data-honesty ruling for the views wave: "V4 RPM-program tiles
// render what the wire carries and stay sparse until the deferred §6
// Connectivity Backend telemetry lands."
//
// WHAT THE WIRE CARRIES TODAY: `GET /v1/gateway/machines` returns the decoded
// status name and NOTHING ELSE — no program, no alarm code/message, no rpm, no
// feed, no part count, no since/elapsed, no heartbeat age. Personally re-read
// at staging time: fleetTelemetry.ts:14-21 (the header's own list) and
// fleetTelemetry.ts:118-125 (`fleetTelemetryFor`, which builds every row from
// `unconnectedTelemetry()` and overwrites ONLY `conn` and `status`).
//
// >>> THE FALLBACK SHAPE, STATED EXPLICITLY. <<< Every block below renders ONLY
// if its own source value is non-null:
//   · the program line only when `t.program` exists;
//   · the spindle/feed line only when `t.spindleRpm` or `t.feedPercent` exists;
//   · the elapsed readout only when `t.elapsed` exists;
//   · the freshness stamp only when `t.since` or `t.heartbeat` exists;
//   · the open-SR readout only when the count is > 0 (that one comes off the
//     Machine record, NOT the gateway, so it is real today).
// A running machine on today's wire has none of the first four, so its tile
// reduces to the SR count if it has one and otherwise to a single muted mono
// line saying live values are not available yet. It never prints an empty unit
// ("RPM · F%" with no numbers, which is what the prototype artboard shows for
// its i2601 tile), never prints a zero it did not receive, and never invents a
// plausible RPM. The richer branches light up with no edit here when §6 lands.
//
// TWO PROTOTYPE STRINGS ARE DELIBERATELY NOT PORTED, both fabrications:
//   · the idle / stopped "reason" text ("Setup — výmena upínača", "Plánovaná
//     údržba — výmena oleja") — no job/reason field exists on the wire or on
//     the Machine record;
//   · "Plánovaný návrat do prevádzky · zajtra 07:00" — invented outright.
// The prototype's "viac ako 60 s" went with them: it is a claim about a SERVER
// threshold (AgentOfflineMonitor) which lives in another repository and which
// this author could not personally verify. Process rule (a). The fix pass then
// took the rest of that sentence for the same reason — see the offline branch.
//
// EVERY STATUS-SHAPED DECISION COMES FROM deriveCardPresentation() — this file
// never tests `status === "running"`, never tests a raw `conn`, and never
// re-derives a state. It DOES read `t.alarms` (the `showAlarmStrip` branch
// tests `.length` and then prints `alarms[0]`), and the earlier wording that
// claimed otherwise was simply false. The read is safe rather than a second
// brain, because `showAlarmStrip` is itself `(isAlarm || offline) &&
// t.alarms.length > 0` (cardStates.ts, deriveCardPresentation — re-read at
// commit time), so the flag ALREADY implies the length test; the branch cannot
// disagree with the contract about whether there is an alarm, only about which
// alarm text to print. Stated accurately so the next reader neither trusts an
// unenforced guarantee nor "fixes" working code to match a wrong comment.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { MachineryV2CardPresentation, MachineryV2Telemetry } from "../cardStates";
import { Label, WallCell, WallExplainer, WallTileFooter } from "./wallTileAtoms";
import { wallStatusInk } from "./wallTileStyles";

export interface WallTileBodyProps {
  presentation: MachineryV2CardPresentation;
  telemetry: MachineryV2Telemetry;
  /** From the Machine record (openServiceReportsCount), NOT from the gateway. */
  openSr: number;
}

const WallTileBody: React.FC<WallTileBodyProps> = ({
  presentation: p,
  telemetry: t,
  openSr,
}) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const lang = getCurrentLanguage();
  const ink = wallStatusInk(p);
  const k = (key: string) => translate("myMachinery:v2.fleet.wall." + key);

  /** The one big readout that is real for every machine on today's wire. */
  const srCell: WallCell | null =
    openSr > 0 ? { label: k("openSr"), value: String(openSr), big: true, size: 28 } : null;

  // ---- unconnected: no agent at all (still ~99% of the fleet) -------------
  if (p.showNoTelemetry) {
    return <WallExplainer label={k("noAgent")} text={k("unconnectedExplainer")} />;
  }

  // ---- awaiting: a connection row exists, the agent has never reported ----
  if (p.showAwaitingBadge) {
    return <WallExplainer label={k("awaitingLabel")} text={k("awaitingExplainer")} />;
  }

  // ---- alarm (live or last-known) ----------------------------------------
  if (p.showAlarmStrip && t.alarms.length > 0) {
    const alarm = t.alarms[0];
    return (
      <>
        <div>
          <Label text={translate("myMachinery:v2.fleet.activeAlarm")} />
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 16,
              color: ink,
              fontWeight: 700,
              marginTop: 2,
            }}
          >
            {/* `alarmLastKnown` — never `staleLook`, which is false for
                offline-with-alarms by design (cardStates.ts). */}
            {p.alarmLastKnown
              ? translate("myMachinery:v2.lastKnown") + " · " + alarm.code
              : alarm.code}
          </div>
          {alarm.message && (
            <div style={{ fontSize: 14, color: "var(--ink)", marginTop: 2, lineHeight: 1.3 }}>
              {alarm.message}
            </div>
          )}
        </div>
        <WallTileFooter
          left={srCell ? { ...srCell, size: 30 } : null}
          right={alarm.since ? { label: k("downSince"), value: alarm.since, size: 18 } : null}
          ink={ink}
        />
      </>
    );
  }

  // ---- offline: the server's own silence verdict --------------------------
  // WHAT THIS BRANCH MAY AND MAY NOT SAY. The wire's `Offline` verdict maps to
  // `{ conn: "live", status: "offline" }` (fleetTelemetry.ts:99) — i.e. the
  // agent IS still heartbeating; cardStates.ts spells it out at
  // deriveCardPresentation's `dotConn` note: "an offline-flagged machine really
  // is reachable". So the copy reports the SERVER'S verdict and the last
  // contact, and asserts nothing about the agent. The prototype's "the edge
  // agent is not responding" sent a technician to debug a healthy appliance;
  // it went for the same reason its "viac ako 60 s" threshold did (process rule
  // (a) applies to the CLAIM, not only to the numeral).
  if (p.state === "offline") {
    return (
      <>
        <div>
          <Label text={k("connectionLost")} />
          <div
            style={{
              fontSize: 15,
              color: "var(--ink)",
              fontWeight: 600,
              marginTop: 2,
              lineHeight: 1.3,
            }}
          >
            {k("offlineExplainer")}
          </div>
        </div>
        {t.heartbeat && (
          <div
            style={{
              marginTop: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 12.5,
              color: "var(--muted)",
            }}
          >
            {k("lastContact")} · {t.heartbeat}
          </div>
        )}
        <WallTileFooter left={null} right={srCell} ink={ink} />
      </>
    );
  }

  // ---- running, WITH a program on the wire --------------------------------
  if (p.showProgramLine && t.program) {
    const metrics = [
      t.spindleRpm != null ? t.spindleRpm.toLocaleString(lang) + " RPM" : null,
      t.feedPercent != null ? "F" + t.feedPercent + "%" : null,
    ].filter(Boolean) as string[];

    return (
      <>
        <div>
          <Label text={k("program")} />
          <div
            style={{
              fontSize: 16,
              color: "var(--ink)",
              fontWeight: 600,
              fontFamily: "var(--font-mono)",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ color: "var(--green)" }}>{"▶ "}</span>
            {t.program}
          </div>
          {t.partCount != null && (
            <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 1 }}>
              {t.partCount.toLocaleString(lang)} {translate("myMachinery:v2.pieces")}
            </div>
          )}
        </div>
        <WallTileFooter
          left={metrics.length > 0 ? { label: k("spindleFeed"), value: metrics.join(" · ") } : null}
          // THE ELAPSED LABEL CARRIES THE LAST-KNOWN PREFIX WHEN THE READING IS
          // ONE, exactly as the alarm branch above does. `showProgramLine` is
          // deliberately TRUE for a stale machine last seen running
          // (cardStates.ts, deriveCardPresentation's showProgramLine note), so
          // once the deferred §6 telemetry lands this tile will print a green
          // ▶ program with live-looking RPM and a counting elapsed — marked
          // only by `opacity: 0.78` and a "?" badge, neither of which is legible
          // from across a hall at this surface's design distance. The word is.
          right={
            t.elapsed
              ? {
                  label: p.staleLook
                    ? translate("myMachinery:v2.lastKnown") + " · " + k("elapsed")
                    : k("elapsed"),
                  value: t.elapsed,
                  big: true,
                }
              : srCell
          }
          ink={ink}
        />
      </>
    );
  }

  // ---- everything else: running-without-a-program, idle, stopped, stale ---
  // The status word is already printed LARGE at the top of the tile, so the
  // body says only what the badge does not — the freshness stamp when the wire
  // carries one and the open-SR count when there is one. THIS IS THE BRANCH
  // EVERY MACHINE LANDS IN TODAY.
  const freshness = t.since
    ? translate("myMachinery:v2.sincePrefix") + " " + t.since
    : t.heartbeat;

  if (!freshness && !srCell) {
    return (
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          color: "var(--muted)",
          letterSpacing: "0.04em",
          lineHeight: 1.35,
        }}
      >
        {k("noLiveData")}
      </div>
    );
  }

  return (
    <WallTileFooter
      left={freshness ? { label: k("lastActivity"), value: freshness, size: 14 } : null}
      right={srCell}
      ink={ink}
    />
  );
};

export default WallTileBody;
